import * as msal from "@azure/msal-browser";
import { ServiceDeskConfig } from "../../Startup/serviceDeskConfig";

export interface MsalAuthenticationService {
  userInfo: any;
}

/**
 * Authorization token provider for a fixed bearer token
 */
export class MsalAuthenticationServiceImplementation implements MsalAuthenticationService {
  private msalInstance: msal.PublicClientApplication;
  private accountInfo: msal.AccountInfo | null;
  private sdConfig: ServiceDeskConfig;
  private isLoginInProgress: boolean;
  public userInfo: any;
  public config: any;
  private idleAfterSeconds: number = 30 * 60; // idle timeout after 30 mins
  private lastTokenRequestTime: Date | null = null; // Added to track the last token request time
  private idleTimeoutInterval: any;
  // @ngInject
  constructor(sdConfig: ServiceDeskConfig) {
    this.accountInfo = null;
    this.msalInstance = null;
    this.sdConfig = sdConfig;
    this.userInfo = {
      idToken: null,
      isAuthenticated: false
    };
    this.isLoginInProgress = false;
    this.config = {
      redirectUri: ""
    };
    this.init();
    this.initializeIdleTimeoutListener();
    this.watchIdleTimeout();
  }

  public async init() {
    var config = this.sdConfig.getMsalConfig();
    this.config.redirectUri = this.sdConfig.redirectUri;
    this.idleAfterSeconds = config.optionalParams.sessiontimeout;
    const msalConfig: msal.Configuration = {
      auth: {
        clientId: config.clientId,
        authority: config.authority,
        postLogoutRedirectUri: config.optionalParams.postLogoutRedirectUri
      },
      cache: {
        cacheLocation: config.optionalParams.cacheLocation
      },
      system: {
        loggerOptions: {
          piiLoggingEnabled: false,
          logLevel: msal.LogLevel.Error,
          loggerCallback: (level: any, message: any, containsPii: boolean): void => {
            if (containsPii) {
              return;
            }

            switch (level) {
              case msal.LogLevel.Error:
                console.log(message);
                return;
            }
          }
        },
        loadFrameTimeout: config.optionalParams.loadFrameTimeout
      }
    };

    this.msalInstance = new msal.PublicClientApplication(msalConfig);

    var accounts = this.msalInstance.getAllAccounts();
    if (accounts && accounts.length == 1) {
      this.accountInfo = accounts[0];
      this.userInfo.isAuthenticated = true;
      this.userInfo.idToken = accounts[0].idTokenClaims;
    }
  }

  private handleResponse(result: msal.AuthenticationResult | null): string | null {
    if (result !== null) {
      console.log("msal redirect promise resolved");
      this.isLoginInProgress = false;
      this.accountInfo = result.account;
      this.userInfo.isAuthenticated = true;
      this.userInfo.idToken = result.account.idTokenClaims;
      return result.idToken;
    }
    return null;
  }

  public handleRedirectPromise() {
    return this.msalInstance.handleRedirectPromise();
  }

  public async loginRedirect() {
    this.msalInstance
      .handleRedirectPromise()
      .then((resp: msal.AuthenticationResult | null) => {
        if (resp !== null) {
          this.handleResponse(resp);
        } else {
          this.isLoginInProgress = true;
          try {
            this.msalInstance
              .loginRedirect({
                scopes: this.sdConfig.msalScopes,
                redirectUri: this.config.redirectUri,
                prompt: "login" // enforce mfa prompt: "select_account"
              })
              .then(function () {
                this.isLoginInProgress = false;
              });
          } catch (err) {
            console.log(err);
            this.isLoginInProgress = false;
          }
        }
      })
      .catch((error) => {
        console.log(error);
      });
  }

  public async _getCachedToken(scopes: any) {
    try {
      this.resetIdleTimer(); //to reset idle time if background tasks running
      let tokenResponse = await this.acquireTokenSilent(scopes);

      return tokenResponse;
    } catch (e) {
      console.log(e);
      return null;
    }
  }

  /**
   * Logs out of current account.
   */
  logout(): void {
    let account: msal.AccountInfo | undefined;
    if (this.accountInfo) {
      account = this.accountInfo;
    }
    const logOutRequest: msal.EndSessionRequest = {
      account
    };

    this.msalInstance.logoutRedirect(logOutRequest);
    this.clearIdleTimeout(); //clear idle time watch
  }

  public async acquireTokenSilent(scopes: any): Promise<string | null> {
    try {
      let tokenResponse = await this.msalInstance.acquireTokenSilent({
        account: this.accountInfo,
        scopes: scopes,
        redirectUri: this.config.redirectUri
      });
      return tokenResponse.accessToken;
    } catch (e) {
      console.log("silent token acquisition fails.");
      try {
        const resp = await this.msalInstance.handleRedirectPromise();
        if (resp !== null) {
          return this.handleResponse(resp);
        } else {
          console.log("acquiring token using redirect");
          await this.msalInstance.acquireTokenRedirect({
            scopes: scopes,
            redirectUri: this.config.redirectUri,
            prompt: "login" // enforce mfa prompt: "select_account"
          });
          // Since redirect will navigate away, we need to explicitly return null
          return null;
        }
      } catch (err) {
        console.error(err);
        return null;
      }
    }
  }

  public loginInProgress() {
    return this.isLoginInProgress;
  }

  public getScopesForEndpoint(endpoint: string, protectedResourceMap: Map<string, any>) {
    if (protectedResourceMap.size > 0) {
      let keys = Array.from(protectedResourceMap.keys());
      for (var i = 0; i < keys.length; i++) {
        var key = keys[i];
        if (endpoint.indexOf(key) > -1) {
          return protectedResourceMap.get(key);
        }
      }
    }
    return null;
  }

  private initializeIdleTimeoutListener() {
    const activityEvents = [/*'mousemove',*/ "keydown", "mousedown" /*, 'touchstart'*/];

    activityEvents.forEach((event) => {
      window.addEventListener(event, this.resetIdleTimer.bind(this), true);
    });
  }

  private resetIdleTimer() {
    this.lastTokenRequestTime = new Date();
  }

  private watchIdleTimeout() {
    this.idleTimeoutInterval = setInterval(() => {
      if (this.lastTokenRequestTime) {
        const currentTime = new Date();
        if (currentTime.getTime() - this.lastTokenRequestTime.getTime() > this.idleAfterSeconds * 1000) {
          console.log("User has been idle for 30 minutes. Logging out...");
          if (this.accountInfo) {
            // Ensure user is logged in before logging out
            this.logout(); // Trigger logout after idle timeout
          }
        }
      }
    }, 1000 * 60); // Check every minute
  }

  private clearIdleTimeout() {
    if (this.idleTimeoutInterval) {
      clearInterval(this.idleTimeoutInterval);
      this.idleTimeoutInterval = null;
    }
  }
}
